import React, { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { basicDetail, statusDrop, commissionTypeDrop, editMode, additionalFields } from "./constant";
import { getFormBaseValidationRule } from "../../../../utils/form/form-utils";
import { Button } from "primereact/button";
import { Container, Grid } from "@mui/material";
import EzControlledDropDowntWithFloatingLabel from "../../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import EzControlledInputWithFloatingLabel from "../../../../common/EzForm/EzControlledInput/WithFloatingLabel";
import EzControlledMultiSelecttWithFloatingLabel from "../../../../common/EzForm/EzControlledMultiSelect/WithFloatingLabel";
import { goBack } from "../../../../utils/gotoback";
import Breadcrumb from "../../../../common/Breadcrumb";
import { useStyles } from "../style";
import { EzToast, TOAST_SEVERITY } from "../../../../common/EzToast";
import { useNavigate } from "react-router-dom";
import { APP_URLS } from "../../../../utils/constant";
import "./styles.css";

const CreateAffiliateComponent = (props) => {
  const navigate = useNavigate();
  const {
    createAffiliate,
    editAffiliate,
    isLoading,
    isCreateSuccess,
    isCreateError,
    isDetailSuccess,
    isDetailError,
    isEditSuccess,
    isEdit,
    formFieldValueMap,
    shouldBlockNavigation,
    locationDD,
    networkDD,
    currencyCodeDD,
  } = props;

  const classes = useStyles();

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    trigger,
    setValue,
    watch,
  } = useForm({
    defaultValues: useMemo(() => {
      return formFieldValueMap;
    }, [formFieldValueMap]),
  });

  const selectedLocationIds = watch("locationId");
  const prevLocationRef = useRef(null);

  // Auto-derive currencyCode when locationId selection changes
  useEffect(() => {
    const prevIds = prevLocationRef.current;
    const currentIds = selectedLocationIds;

    // Skip if no currencyCodeDD yet or if the value hasn't actually changed
    if (!currencyCodeDD || currencyCodeDD.length === 0) return;
    if (JSON.stringify(prevIds) === JSON.stringify(currentIds)) return;

    prevLocationRef.current = currentIds;

    if (!currentIds || currentIds.length === 0) {
      setValue("currencyCode", "");
      return;
    }

    const codes = currentIds
      .map((id) => currencyCodeDD.find((item) => item.id === id)?.currencyCode)
      .filter(Boolean);

    // De-duplicate while preserving order
    const unique = [...new Set(codes)];
    setValue("currencyCode", unique.join(","));
  }, [selectedLocationIds, currencyCodeDD, setValue]);

  const affiliateName = formFieldValueMap?.affiliateName || "New Affiliate";

  const breadcrumbD = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/affiliate", label: "Affiliate" },
    { path: "#", label: affiliateName },
  ];

  const navigateToAffiliateScreen = () => {
    navigate(APP_URLS.AFFILIATE.HOME);
  };

  useEffect(() => {
    if (!isCreateSuccess && !isEditSuccess) {
      shouldBlockNavigation(isDirty);
    }
  }, [isDirty, shouldBlockNavigation, isCreateSuccess, isEditSuccess]);

  useEffect(() => {
    if (isCreateSuccess) {
      reset();
    }
  }, [isCreateSuccess, reset]);

  useEffect(() => {
    if (isDetailSuccess) {
      reset({ ...formFieldValueMap });
    }
  }, [isDetailSuccess, formFieldValueMap, reset]);

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const requiredFields = [
    "affiliateName",
    "affiliateCode",
    "email",
    "address",
    "contactNumber",
    "status",
  ];

  const onSubmit = async (data) => {
    const isValid = await trigger(requiredFields);
    if (!isValid) return;

    const commissionType = data.commissionType;
    const commissionTypeValue = parseFloat(data.commissionTypeValue);

    const payload = {
      ...data,
      userId: localStorage.getItem("userId"),
      status: data.status === 1 ? 1 : 0,
      locationId: data.locationId || [],
      networkId: data.networkId || [],
      commissionType: commissionType || null,
      commissionTypeValue: isNaN(commissionTypeValue) ? null : commissionTypeValue,
    };

    if (isEdit) {
      editAffiliate({ id: data.id, data: payload });
    } else {
      createAffiliate(payload);
    }
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">
              {isEdit ? "Update Affiliate" : "Create Affiliate"}
            </h4>
          </div>
        <div className="buttonarea">
          <Button
            tooltip={"Back to Affiliate"}
            tooltipOptions={{ position: "left" }}
            label={"Back to Affiliate"}
            className={`p-button `}
            style={{ borderRadius: "10px" }}
            onClick={navigateToAffiliateScreen}
          />
        </div>
        </div>
      </div>
    );
  };

  return (
    <Container maxWidth="lg">
      <Breadcrumb list={breadcrumbD} />
      <div className={`containerbox ${classes.bg}`}>
        {renderPageHeaderSection()}
        <div className="form-demo">
          <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <Grid container spacing={2} style={{ marginTop: "20px" }}>
              {basicDetail?.map((item) =>
                item.name === "status" ? (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.name}>
                    <EzControlledDropDowntWithFloatingLabel
                      name={item?.name}
                      control={control}
                      rules={item?.rule}
                      optionLabel={"name"}
                      optionValue={"id"}
                      options={statusDrop}
                      isError={errors[item?.name]}
                      errorMsg={getFormErrorMessage(item?.name)}
                      label={item?.label}
                      labelClassName={"ml-2"}
                    />
                  </Grid>
                ) : item.name === "commissionType" ? (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.name}>
                    <EzControlledDropDowntWithFloatingLabel
                      name={item?.name}
                      control={control}
                      rules={item?.rule}
                      optionLabel={"name"}
                      optionValue={"id"}
                      options={commissionTypeDrop}
                      isError={errors[item?.name]}
                      errorMsg={getFormErrorMessage(item?.name)}
                      label={item?.label}
                      labelClassName={"ml-2"}
                    />
                  </Grid>
                ) : item.name === "commissionTypeValue" ? (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.name}>
                    <EzControlledInputWithFloatingLabel
                      name={item?.name}
                      control={control}
                      label={item?.label}
                      rules={{
                        required: item?.rule?.required || false,
                        validate: (value) => {
                          const num = parseFloat(value);
                          if (isNaN(num)) return true;
                          
                          const commissionType = watch("commissionType");
                          
                          if (commissionType === "percent") {
                            if (num < 0) return "Percentage cannot be negative";
                            if (num > 100) return "Percentage cannot exceed 100%";
                          }
                          
                          if (commissionType === "amount") {
                            if (num < 0) return "Amount cannot be negative";
                          }
                          
                          return true;
                        }
                      }}
                      type="number"
                      isError={errors[item?.name]}
                      errorMsg={getFormErrorMessage(item?.name)}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.name}>
                    <EzControlledInputWithFloatingLabel
                      name={item?.name}
                      control={control}
                      label={item?.label}
                      rules={item?.rule}
                      type={item?.type || "text"}
                      disabled={item?.disabled}
                      isError={errors[item?.name]}
                      errorMsg={getFormErrorMessage(item?.name)}
                    />
                  </Grid>
                )
              )}
              
              {additionalFields?.map((item) =>
                item.type === "multiselect" ? (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.name}>
                    <EzControlledMultiSelecttWithFloatingLabel
                      name={item?.name}
                      control={control}
                      rules={item?.rule}
                      options={
                        item.name === "locationId"
                          ? locationDD || []
                          : item.name === "networkId"
                          ? networkDD || []
                          : []
                      }
                      optionLabel="name"
                      optionValue="id"
                      isError={errors[item?.name]}
                      errorMsg={getFormErrorMessage(item?.name)}
                      label={item?.label}
                      labelClassName={"ml-2"}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.name}>
                    <EzControlledInputWithFloatingLabel
                      name={item?.name}
                      control={control}
                      label={item?.label}
                      rules={item?.rule}
                      type={item?.type || "text"}
                      disabled={item?.disabled}
                      isError={errors[item?.name]}
                      errorMsg={getFormErrorMessage(item?.name)}
                    />
                  </Grid>
                )
              )}
            </Grid>

            <div className={"make-flex mt-3 mr-2"}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                <div style={{ margin: "15px 0px 15px 10px", minWidth: "100px" }}>
                  <Button
                    type="submit"
                    label={isEdit ? "Update" : "Save"}
                    className="p-button-raised"
                  />
                </div>
              </div>
            </div>

            {isEdit && (
              <Grid className="modsection" container spacing={2}>
                {editMode?.map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.name}>
                    <EzControlledInputWithFloatingLabel
                      name={item?.name}
                      control={control}
                      disabled={item?.disabled}
                      label={item?.label}
                      type={item?.type}
                      rules={item?.rule}
                      isError={errors[item?.name]}
                      errorMsg={getFormErrorMessage(item?.name)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </form>
        </div>
      </div>
    </Container>
  );
};

export default CreateAffiliateComponent;
